/**
 * Conversion Modal (Omvandling mellan aktieslag)
 *
 * Modal for registering share class conversion transactions.
 * Converts shares from one share class to another according to
 * the company's conversion clause (omvandlingsförbehåll).
 */

import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowRightLeft, Info } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import { Button } from "@/components/ui/button";
import {
  useShareClasses,
  useShareholdersSummary,
  useShareholderHoldings,
  useRegisterConversion,
} from "@/hooks/use-share-book";
import type { ShareClass, ShareholderWithHoldings, ShareHolding } from "@/lib/api";

interface ConversionModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  hasConversionClause: boolean;
}

const conversionSchema = z.object({
  shareholderId: z.string().min(1, "Välj aktieägare"),
  fromShareClassId: z.string().min(1, "Välj aktieslag att konvertera från"),
  toShareClassId: z.string().min(1, "Välj aktieslag att konvertera till"),
  numberFrom: z.coerce.number().min(1, "Aktienummer från krävs"),
  numberTo: z.coerce.number().min(1, "Aktienummer till krävs"),
  transactionDate: z.string().min(1, "Transaktionsdatum krävs"),
  notes: z.string().optional(),
}).refine((data) => data.numberTo >= data.numberFrom, {
  message: "Aktienummer till måste vara större än eller lika med från",
  path: ["numberTo"],
}).refine((data) => data.fromShareClassId !== data.toShareClassId, {
  message: "Från och till aktieslag kan inte vara samma",
  path: ["toShareClassId"],
});

type ConversionFormData = z.infer<typeof conversionSchema>;

export function ConversionModal({
  isOpen,
  onClose,
  companyId,
  hasConversionClause,
}: ConversionModalProps) {
  const { data: shareClasses = [] } = useShareClasses(companyId);
  const { data: shareholders = [] } = useShareholdersSummary(companyId);
  const registerConversion = useRegisterConversion();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ConversionFormData>({
    resolver: zodResolver(conversionSchema),
    defaultValues: {
      shareholderId: "",
      fromShareClassId: "",
      toShareClassId: "",
      numberFrom: undefined,
      numberTo: undefined,
      transactionDate: new Date().toISOString().split("T")[0],
      notes: "",
    },
  });

  const watchShareholderId = watch("shareholderId");
  const watchFromShareClassId = watch("fromShareClassId");
  const watchToShareClassId = watch("toShareClassId");
  const watchNumberFrom = watch("numberFrom");
  const watchNumberTo = watch("numberTo");

  // Fetch holdings for selected shareholder
  const { data: holdings = [] } = useShareholderHoldings(
    watchShareholderId || undefined
  );

  // Active shareholders
  const activeShareholders = useMemo(() => {
    return shareholders.filter((s: ShareholderWithHoldings) => s.isActive);
  }, [shareholders]);

  // Get available holdings for selected shareholder in from share class
  const availableHoldings = useMemo(() => {
    if (!watchFromShareClassId || !holdings.length) return [];
    return holdings.filter(
      (h: ShareHolding) => h.shareClassId === watchFromShareClassId
    );
  }, [holdings, watchFromShareClassId]);

  // Get from share class info
  const fromShareClass = useMemo(() => {
    return shareClasses.find((sc: ShareClass) => sc.id === watchFromShareClassId);
  }, [shareClasses, watchFromShareClassId]);

  // Get to share class info
  const toShareClass = useMemo(() => {
    return shareClasses.find((sc: ShareClass) => sc.id === watchToShareClassId);
  }, [shareClasses, watchToShareClassId]);

  // Calculate quantity
  const calculatedQuantity = useMemo(() => {
    if (!watchNumberFrom || !watchNumberTo) return 0;
    return Math.max(0, watchNumberTo - watchNumberFrom + 1);
  }, [watchNumberFrom, watchNumberTo]);

  const onSubmit = async (data: ConversionFormData) => {
    try {
      await registerConversion.mutateAsync({
        companyId,
        data: {
          shareholderId: data.shareholderId,
          fromShareClassId: data.fromShareClassId,
          toShareClassId: data.toShareClassId,
          quantity: calculatedQuantity,
          numberFrom: data.numberFrom,
          numberTo: data.numberTo,
          transactionDate: data.transactionDate,
          notes: data.notes,
        },
      });
      toast.success(
        `Omvandling registrerad - ${calculatedQuantity.toLocaleString("sv-SE")} aktier`
      );
      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Kunde inte registrera omvandling";
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Need at least 2 share classes to convert
  if (shareClasses.length < 2) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Omvandling" size="md">
        <div className="p-6 text-center">
          <ArrowRightLeft className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Kan inte genomföra omvandling
          </h3>
          <p className="text-gray-600">
            Det krävs minst två aktieslag för att kunna omvandla aktier. Skapa
            fler aktieslag först.
          </p>
          <Button className="mt-6" onClick={handleClose}>
            Stäng
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Omvandling mellan aktieslag"
      size="lg"
    >
      <div className="space-y-6">
        {/* Warning if no conversion clause */}
        {!hasConversionClause && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Info className="text-amber-600 mt-0.5 flex-shrink-0" size={20} />
              <div>
                <p className="font-medium text-amber-900">
                  Omvandlingsförbehåll saknas
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  Bolagsordningen innehåller inget omvandlingsförbehåll. Kontrollera
                  att omvandlingen är tillåten enligt bolagsordningen.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info box */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <ArrowRightLeft className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <p className="font-medium text-blue-900">Vad är omvandling?</p>
              <p className="text-sm text-blue-700 mt-1">
                Omvandling innebär att aktier av ett aktieslag byts ut mot aktier
                av ett annat aktieslag (t.ex. B-aktier till A-aktier). Villkoren
                för omvandling regleras i bolagsordningen.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Shareholder */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aktieägare *
            </label>
            <select {...register("shareholderId")} className="input">
              <option value="">Välj aktieägare</option>
              {activeShareholders.map((s: ShareholderWithHoldings) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.totalShares.toLocaleString("sv-SE")} aktier totalt)
                </option>
              ))}
            </select>
            {errors.shareholderId && (
              <p className="text-sm text-red-600 mt-1">
                {errors.shareholderId.message}
              </p>
            )}
          </div>

          {/* From Share Class */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Omvandla från aktieslag *
            </label>
            <select {...register("fromShareClassId")} className="input">
              <option value="">Välj aktieslag</option>
              {shareClasses.map((sc: ShareClass) => (
                <option key={sc.id} value={sc.id}>
                  {sc.name} ({sc.code}) - Röstvärde: {sc.votingPower}
                </option>
              ))}
            </select>
            {errors.fromShareClassId && (
              <p className="text-sm text-red-600 mt-1">
                {errors.fromShareClassId.message}
              </p>
            )}
          </div>

          {/* To Share Class */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Omvandla till aktieslag *
            </label>
            <select {...register("toShareClassId")} className="input">
              <option value="">Välj aktieslag</option>
              {shareClasses
                .filter((sc: ShareClass) => sc.id !== watchFromShareClassId)
                .map((sc: ShareClass) => (
                  <option key={sc.id} value={sc.id}>
                    {sc.name} ({sc.code}) - Röstvärde: {sc.votingPower}
                  </option>
                ))}
            </select>
            {errors.toShareClassId && (
              <p className="text-sm text-red-600 mt-1">
                {errors.toShareClassId.message}
              </p>
            )}
          </div>

          {/* Conversion preview */}
          {fromShareClass && toShareClass && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-center gap-4 text-sm">
                <div className="text-center">
                  <p className="font-medium text-gray-900">{fromShareClass.name}</p>
                  <p className="text-gray-500">Röstvärde: {fromShareClass.votingPower}</p>
                </div>
                <ArrowRightLeft className="text-gray-400" size={24} />
                <div className="text-center">
                  <p className="font-medium text-gray-900">{toShareClass.name}</p>
                  <p className="text-gray-500">Röstvärde: {toShareClass.votingPower}</p>
                </div>
              </div>
            </div>
          )}

          {/* Available Holdings Info */}
          {watchShareholderId && watchFromShareClassId && availableHoldings.length > 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
                <div className="text-sm">
                  <p className="font-medium text-blue-900">
                    Tillgängliga aktier att omvandla:
                  </p>
                  <ul className="mt-1 space-y-1">
                    {availableHoldings.map((h: ShareHolding, idx: number) => (
                      <li key={idx} className="text-blue-700">
                        Nr {h.numberFrom} - {h.numberTo} (
                        {h.numberTo - h.numberFrom + 1} st)
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Share Number Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Aktienummer från *
              </label>
              <input
                type="number"
                {...register("numberFrom")}
                className="input"
                min={1}
              />
              {errors.numberFrom && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.numberFrom.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Aktienummer till *
              </label>
              <input
                type="number"
                {...register("numberTo")}
                className="input"
                min={1}
              />
              {errors.numberTo && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.numberTo.message}
                </p>
              )}
            </div>
          </div>

          {calculatedQuantity > 0 && (
            <p className="text-sm text-gray-600">
              Antal aktier som omvandlas:{" "}
              <span className="font-medium">
                {calculatedQuantity.toLocaleString("sv-SE")}
              </span>
            </p>
          )}

          {/* Transaction Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transaktionsdatum *
            </label>
            <input
              type="date"
              {...register("transactionDate")}
              className="input"
            />
            {errors.transactionDate && (
              <p className="text-sm text-red-600 mt-1">
                {errors.transactionDate.message}
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Anteckningar
            </label>
            <textarea
              {...register("notes")}
              className="input min-h-[80px]"
              placeholder="Valfria anteckningar om omvandlingen..."
            />
          </div>

          {/* Legal info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <strong>Observera:</strong> Omvandling sker enligt villkoren i
              bolagsordningen. Kontrollera att aktieägaren har rätt att begära
              omvandling.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleClose}>
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || calculatedQuantity === 0}
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Registrerar...
                </>
              ) : (
                "Registrera omvandling"
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
